import { Button } from "@/components/ui/button";
import ShopifyFAQ from "@/components/ShopifyFAQ";
import EmailReviewsSection from "@/components/EmailReviewsSection";
import WebshopScreenshotsMarquee from "@/components/WebshopScreenshotsMarquee";


import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Helmet } from "react-helmet-async";

import WhyNextconvertSection from "@/components/WhyNextconvertSection";
import WebshopLeadSection from "@/components/WebshopLeadSection";
import SamenGroeienMarquee from "@/components/SamenGroeienMarquee";
import CroCalculator from "@/components/CroCalculator";
import USPSection from "@/components/USPSection";
import uspWebshopImg from "@/assets/usp-webshop-shopify.png";
import forevadifferent from "@/assets/portfolio/forevadifferent-square.webp";
import diamonddetailing from "@/assets/portfolio/diamonddetailing-square.webp";
import TrustLogosSection from "@/components/TrustLogosSection";
import { Star } from "lucide-react";
import shopifyBagLogo from "@/assets/shopify-bag-logo.png";
import { scrollToPlanner } from "@/lib/scrollToPlanner";

const Webshops = () => {
  const openWhatsAppChat = () => {
    const message = encodeURIComponent("Hallo Nextconvert, ik heb een vraag over jullie webshop diensten.");
    window.open(`https://wa.me/31620192358?text=${message}`, "_blank");
  };
  return <div className="flex flex-col bg-cream">
      
      <SEO title="Shopify webshop laten maken | Webshop laten bouwen" description="Shopify webshop laten maken of laten bouwen? Wij bouwen webshops die converteren voor DTC-merken. Bekijk de mogelijkheden, kosten en resultaten van 30+ merken." canonical="/shopify-webshop" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Shopify webshop laten maken",
            "provider": {
              "@type": "Organization",
              "name": "Nextconvert",
              "url": "https://nextconvert.nl"
            },
            "description": "Complete Shopify webshops die converteren. Ontwerp, ontwikkeling en optimalisatie voor DTC-merken.",
            "areaServed": "NL",
            "url": "https://nextconvert.nl/shopify-webshop"
          })}
        </script>
      </Helmet>
      
      {/* Hero Banner - Centered like homepage */}
      <section className="bg-primary w-full relative overflow-hidden">
        {/* Floating decorative images - desktop only */}
        <div className="hidden lg:block">
          <img
            src={forevadifferent}
            alt=""
            className="absolute left-[5%] bottom-[15%] w-48 xl:w-56 rounded-2xl object-cover shadow-2xl rotate-[-4deg] opacity-90"
            loading="eager"
          />
          <img
            src={diamonddetailing}
            alt=""
            className="absolute right-[4%] top-[12%] w-44 xl:w-52 rounded-2xl object-cover shadow-2xl rotate-[3deg] opacity-90"
            loading="eager"
          />
        </div>

        <div className="max-w-[820px] mx-auto px-5 pt-14 pb-8 md:pt-28 md:pb-16 flex flex-col items-center text-center relative z-10">
          <h1 className="font-bold tracking-tight text-white leading-[1.1] mb-5 text-4xl md:text-5xl lg:text-[3.1rem]">
            Verhoog de{" "}
            <span className="text-accent accent-serif">winstgevendheid</span>{" "}
            <span className="whitespace-nowrap">van je</span>{" "}
            <span className="inline-flex items-center gap-2 md:gap-3">
              Shopify-webshop
              <img src={shopifyBagLogo} alt="Shopify logo" className="hidden md:inline h-9 md:h-11 lg:h-12 w-auto" loading="eager" />
            </span>
            , zonder meer uit te geven aan advertenties.
          </h1>


          <p className="text-white/70 text-sm md:text-base lg:text-lg leading-relaxed max-w-[580px] mb-7">
            Wij optimaliseren jouw volledige Shopify-funnel om conversieratio, orderwaarde en omzet per bezoeker structureel te verhogen. Van productpagina en offer tot cart en checkout, iedere optimalisatie is gericht op meer rendement uit hetzelfde verkeer.
          </p>

          <div className="flex items-center justify-center gap-3 md:gap-4 mb-10">
            <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-7 md:px-8 h-11 md:h-12 rounded-full text-sm md:text-base">
              <button onClick={scrollToPlanner}>Plan jouw gratis strategiesessie</button>
            </Button>
          </div>
          {/* Google Reviews indicator */}
          <div className="flex items-center gap-3">
            <img alt="Google" className="w-9 h-9 rounded-lg object-cover" src="/__l5e/assets-v1/58ce976d-cbb5-4e59-b01e-11eacfe84dc9/google-icon.jpg" />
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}
              </div>
              <span className="text-white/60 text-xs">Beoordeeld met een 5.0</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Logos Section */}
      <TrustLogosSection />

      {/* USP Section */}
      <USPSection
        label="Wij optimaliseren jouw CRO"
        title="Meer rendement uit iedere bezoeker én klant."
        description="Meer verkeer inkopen is niet altijd de oplossing. Wij zorgen dat je Shopify webshop meer haalt uit het verkeer dat je al hebt, met focus op conversie, orderwaarde en winstgevende groei."
        ctaCard={{
          text: (
            <>
              <strong className="text-white">We hebben al 30+ DTC-merken geholpen</strong> aan meer omzet uit hun bestaande verkeer. Geen giswerk, maar een concreet en bewezen plan dat je winstgevendheid verhoogt.
            </>
          ),
          button: { text: "Vraag gratis audit aan", onClick: scrollToPlanner },
        }}
        usps={[
          { label: "Hogere conversie", title: "Meer bezoekers omzetten in klanten", description: "We verminderen frictie en versterken wat bezoekers nodig hebben om te kopen. Van productpagina en navigatie tot winkelwagen en checkout." },
          { label: "Hogere orderwaarde", title: "Meer omzet uit iedere bestelling", description: "Met slimme bundels, upsells en productaanbevelingen verhogen we de waarde van iedere bestelling, zonder extra advertentiebudget." },
          { label: "Winstgevende groei", title: "Meer rendement uit je bestaande verkeer", description: "Conversie en orderwaarde komen samen in één doel: meer omzet uit hetzelfde verkeer en een webshop die klaar is om te schalen." },
          { label: "Doorlopende optimalisatie", title: "We stoppen niet na de livegang", description: "We meten, testen en verbeteren continu op basis van data, zodat je groei structureel is en niet eenmalig." },
        ]}
      />



      {/* Planner Section */}
      <WebshopLeadSection
        title={<>Plan jouw gratis <span className="text-accent">strategie gesprek</span> in.</>}
        description="In een gratis kennismaking laten we je zien hoe we meer omzet genereren uit je bestaande traffic, zonder dat je meer advertentiekosten hoeft te maken. We analyseren je funnel, identificeren de grootste groeikansen en geven je een concreet plan om direct meer uit je webshop te halen."
      />

      {/* Reviews Section */}
      <EmailReviewsSection variant="webshop" />

      {/* Why Nextconvert Section */}
      <WhyNextconvertSection />

      {/* Webshop screenshots marquee */}
      <WebshopScreenshotsMarquee />

      <CroCalculator />

      {/* FAQ Section */}
      <ShopifyFAQ />




      <SamenGroeienMarquee />
    </div>;
};
export default Webshops;